'use client'

import React, {
  Suspense,
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { CLIENT_PAGES } from '@/constants/clientPortalPages'
import { useClientPartners } from '@/hooks/useClientPartners'
import { useOrgDashboardAccess, type OrgDashboardAccess } from '@/hooks/useOrgDashboardAccess'
import { useOrgProgramTree } from '@/hooks/useOrgProgramTree'
import { useClientSelection } from '@/providers/ClientSelectionContext'
import { ClientPortalSessionLengthEffect } from '@/providers/ClientPortalSessionLengthEffect'
import { ClientFeatureFlagsProvider, type ClientFeatureFlagsState } from '@/providers/LaunchDarkly'
import { syncPostHogPartnerGroup } from '@/utilities/analytics/partnerGroupAnalytics'
import {
  buildDashboardHrefFromSelection,
  isDashboardNavPath,
  isOrgDashboardPath,
  resolveDashboardNavigationState,
  resolveDashboardRouteState,
  resolveProgramDashboardSource,
  type DashboardRouteState,
  type ProgramDashboardSource,
} from '@/utilities/dashboard/dashboardContext'
import {
  getOrgDashboardMetricVisibility,
  ORG_DASHBOARD_FLAG,
  parseOrgDashboardFeatureFlag,
  resolveClientPortalRouteIntent,
  type ClientFeatureFlagContext,
  type OrgDashboardMetricVisibility,
  type OrgDashboardVariant,
} from '@/utilities/client/clientPortalContext'
import {
  buildOrgProgramSwitcherTree,
  resolveOrgPrimaryPartnerIdForPartner,
  resolveSwitcherSelection,
  resolveSwitcherSelectionPartnerId,
  type SwitcherOrganization,
  type SwitcherSelection,
} from '@/utilities/dashboard/orgProgramSwitcherTree'
import {
  buildOrgDashboardUrl,
  buildProgramContextDashboardUrl,
} from '@/utilities/dashboard/orgProgramContextUrl'
import { resolveProgramPartnerIdFromAllowedPartners } from '@/utilities/dashboard/programDashboardUrl'
import { normalizeNullableClientId } from '@/utilities/auth/authHelper'
import type { CachedPartner } from '@/utilities/partnersCache'

type ClientPortalContextValue = {
  access: OrgDashboardAccess
  dashboardRouteState: DashboardRouteState | null
  programDashboardState: ProgramDashboardSource | null
  organizations: SwitcherOrganization[]
  partners: CachedPartner[]
  partnersLoading: boolean
  selection: SwitcherSelection | null
  capabilityPartnerId: string | null
  dashboardHref: string
  isDashboardPath: boolean
  isDashboardRoute: boolean
  isOrgContext: boolean
  isProgramScopedNavDisabled: boolean
  isProgramScopedRouteRedirecting: boolean
  showNestedSwitcher: boolean
  showFlatSwitcher: boolean
  switcherLoading: boolean
  orgDashboardVariant: OrgDashboardVariant
  orgDashboardMetricVisibility: OrgDashboardMetricVisibility
}

type ResolvedPortalContext = {
  context: ClientFeatureFlagContext
  isOrgDashboardFlagEnabled: boolean
  orgDashboardVariant: OrgDashboardVariant
}

const ClientPortalContext = createContext<ClientPortalContextValue | undefined>(undefined)

function SearchParamsSync({
  pathname,
  onChange,
}: Readonly<{
  pathname: string
  onChange: (pathname: string, searchQuery: string) => void
}>) {
  const searchQuery = useSearchParams().toString()

  useLayoutEffect(() => {
    onChange(pathname, searchQuery)
  }, [onChange, pathname, searchQuery])

  return null
}

function findPartner(partners: readonly CachedPartner[], partnerId: string | null | undefined) {
  const normalizedPartnerId = normalizeNullableClientId(partnerId)
  if (!normalizedPartnerId) return undefined
  return partners.find((partner) => partner.partnerId === normalizedPartnerId)
}

function getPartnerFlags(partner: CachedPartner | undefined): Record<string, unknown> {
  return Object.fromEntries((partner?.featureFlags ?? []).map((flag) => [flag.name, flag.value]))
}

function getPartnerOrgDashboardVariant(partner: CachedPartner | undefined): OrgDashboardVariant {
  return parseOrgDashboardFeatureFlag(
    partner?.featureFlags?.find((flag) => flag.name === ORG_DASHBOARD_FLAG)?.value,
  )
}

function partnerHasOrgDashboard(partner: CachedPartner | undefined): boolean {
  return getPartnerOrgDashboardVariant(partner) !== null
}

/**
 * Canonical Client Portal context. `/partners` is the program allowlist and the
 * source for partner-scoped flags/capabilities; `/organizations` adds hierarchy
 * and primary-partner metadata. Pages and navbar consumers read this one result.
 */
export function ClientPortalContextProvider({
  children,
  enabled = true,
}: Readonly<{ children: React.ReactNode; enabled?: boolean }>) {
  const pathname = usePathname()
  const router = useRouter()
  const [routeLocation, setRouteLocation] = useState<{
    pathname: string
    searchQuery: string
  } | null>(null)
  const lastCanonicalCommandRef = useRef<string | null>(null)
  const lastSyncedOrgGroupRef = useRef<string | null>(null)
  const { selectedClientId, setSelectedClientId } = useClientSelection()
  const isRouteReady = !enabled || routeLocation?.pathname === pathname
  const currentSearchQuery = isRouteReady ? (routeLocation?.searchQuery ?? '') : ''
  const syncRouteLocation = useCallback((nextPathname: string, nextSearchQuery: string) => {
    setRouteLocation((current) => {
      if (current?.pathname === nextPathname && current.searchQuery === nextSearchQuery) {
        return current
      }
      return { pathname: nextPathname, searchQuery: nextSearchQuery }
    })
  }, [])
  const isDashboardPath = isOrgDashboardPath(pathname)
  const isDashboardRoute = isDashboardNavPath(pathname)
  const dataEnabled = enabled && isRouteReady

  const {
    partners,
    fetchSettled: partnersFetchSettled,
    loadFailed: partnersLoadFailed,
    loading: partnersLoading,
    error: partnersError,
    refetch: refetchPartners,
  } = useClientPartners({ enabled: dataEnabled })
  const allowedPartnerIds = useMemo(() => partners.map((partner) => partner.partnerId), [partners])

  const routeIntent = useMemo(
    () =>
      resolveClientPortalRouteIntent({
        pathname,
        searchParams: new URLSearchParams(currentSearchQuery),
        selectedClientId,
      }),
    [currentSearchQuery, pathname, selectedClientId],
  )

  const programDashboardState = useMemo((): ProgramDashboardSource => {
    if (routeIntent.kind !== 'program') return { kind: 'loading' }
    return resolveProgramDashboardSource({
      searchParams: new URLSearchParams(currentSearchQuery),
      selectedClientId,
      allowedPartnerIds,
      isLoading: !isRouteReady || partnersLoading || !partnersFetchSettled,
      loadFailed: partnersLoadFailed,
    })
  }, [
    allowedPartnerIds,
    currentSearchQuery,
    isRouteReady,
    partnersFetchSettled,
    partnersLoadFailed,
    partnersLoading,
    routeIntent.kind,
    selectedClientId,
  ])

  const fallbackProgramId = useMemo(
    () => resolveProgramPartnerIdFromAllowedPartners(allowedPartnerIds, null, selectedClientId),
    [allowedPartnerIds, selectedClientId],
  )
  const hasAnyOrgDashboardPartner = partners.some(partnerHasOrgDashboard)
  const preOrgPartner = useMemo(() => {
    if (routeIntent.kind === 'org') return findPartner(partners, routeIntent.partnerId)
    if (routeIntent.kind === 'program' && programDashboardState.kind === 'program') {
      return findPartner(partners, programDashboardState.partnerId)
    }
    if (routeIntent.kind === 'dashboard-default') {
      return findPartner(partners, routeIntent.selectedPartnerId)
    }
    if (routeIntent.kind === 'selected') return findPartner(partners, routeIntent.partnerId)
    return undefined
  }, [partners, programDashboardState, routeIntent])
  const shouldLoadOrganizations =
    dataEnabled &&
    (routeIntent.kind === 'dashboard-default'
      ? hasAnyOrgDashboardPartner
      : partnerHasOrgDashboard(preOrgPartner))
  const {
    rawOrganizations,
    bootstrapPartnerId,
    fetchSettled: orgsFetchSettled,
    loadFailed: orgsLoadFailed,
    loading: orgsLoading,
  } = useOrgProgramTree({
    enabled: shouldLoadOrganizations,
    selectedClientId,
    allowedPartnerIds,
  })
  const dashboardProgramId =
    routeIntent.kind === 'program' && programDashboardState.kind === 'program'
      ? programDashboardState.partnerId
      : fallbackProgramId

  const resolvedPortalContext = useMemo<ResolvedPortalContext | null>(() => {
    // The org dashboard layout is org-wide, so it uses the primary partner's variant.
    const getOrgPrimaryPartnerOrgDashboardVariant = (partner: CachedPartner) =>
      getPartnerOrgDashboardVariant(
        findPartner(
          partners,
          resolveOrgPrimaryPartnerIdForPartner(rawOrganizations, partner.partnerId),
        ) ?? partner,
      )

    if (!isRouteReady || partnersLoading || !partnersFetchSettled) return null
    if (partnersLoadFailed) {
      return {
        context: { kind: 'none' },
        isOrgDashboardFlagEnabled: false,
        orgDashboardVariant: null,
      }
    }

    if (routeIntent.kind === 'program') {
      if (programDashboardState.kind !== 'program') {
        return {
          context: { kind: 'none' },
          isOrgDashboardFlagEnabled: false,
          orgDashboardVariant: null,
        }
      }
      const partner = findPartner(partners, programDashboardState.partnerId)
      return {
        context: { kind: 'partner', partnerId: programDashboardState.partnerId },
        isOrgDashboardFlagEnabled: partnerHasOrgDashboard(partner),
        orgDashboardVariant: getPartnerOrgDashboardVariant(partner),
      }
    }

    if (routeIntent.kind === 'org') {
      const orgPartner = findPartner(partners, routeIntent.partnerId)
      if (orgPartner && partnerHasOrgDashboard(orgPartner)) {
        return {
          context: { kind: 'partner', partnerId: orgPartner.partnerId },
          isOrgDashboardFlagEnabled: true,
          orgDashboardVariant: getOrgPrimaryPartnerOrgDashboardVariant(orgPartner),
        }
      }
      const fallbackPartner = findPartner(partners, fallbackProgramId)
      return {
        context: fallbackPartner
          ? { kind: 'partner', partnerId: fallbackPartner.partnerId }
          : { kind: 'none' },
        isOrgDashboardFlagEnabled: false,
        orgDashboardVariant: null,
      }
    }

    if (routeIntent.kind === 'dashboard-default') {
      if (!hasAnyOrgDashboardPartner) {
        const fallbackPartner = findPartner(partners, fallbackProgramId)
        return {
          context: fallbackPartner
            ? { kind: 'partner', partnerId: fallbackPartner.partnerId }
            : { kind: 'none' },
          isOrgDashboardFlagEnabled: false,
          orgDashboardVariant: null,
        }
      }
      if (orgsLoading || !orgsFetchSettled) return null
      const bootstrapPartner = findPartner(partners, bootstrapPartnerId)
      if (!orgsLoadFailed && bootstrapPartner && partnerHasOrgDashboard(bootstrapPartner)) {
        return {
          context: { kind: 'partner', partnerId: bootstrapPartner.partnerId },
          isOrgDashboardFlagEnabled: true,
          orgDashboardVariant: getOrgPrimaryPartnerOrgDashboardVariant(bootstrapPartner),
        }
      }
      const fallbackPartner = findPartner(partners, fallbackProgramId)
      return {
        context: fallbackPartner
          ? { kind: 'partner', partnerId: fallbackPartner.partnerId }
          : { kind: 'none' },
        isOrgDashboardFlagEnabled: false,
        orgDashboardVariant: null,
      }
    }

    const contextPartner = findPartner(partners, routeIntent.partnerId)
    return {
      context: contextPartner
        ? { kind: 'partner', partnerId: contextPartner.partnerId }
        : { kind: 'none' },
      isOrgDashboardFlagEnabled: partnerHasOrgDashboard(contextPartner),
      orgDashboardVariant: getPartnerOrgDashboardVariant(contextPartner),
    }
  }, [
    bootstrapPartnerId,
    fallbackProgramId,
    hasAnyOrgDashboardPartner,
    isRouteReady,
    orgsFetchSettled,
    orgsLoadFailed,
    orgsLoading,
    partners,
    partnersFetchSettled,
    partnersLoadFailed,
    partnersLoading,
    programDashboardState,
    rawOrganizations,
    routeIntent,
  ])

  const access = useOrgDashboardAccess({
    enabled,
    isFlagReady: resolvedPortalContext !== null,
    isFlagEnabled: resolvedPortalContext?.isOrgDashboardFlagEnabled ?? false,
  })
  const orgDashboardVariant = resolvedPortalContext?.orgDashboardVariant ?? null
  const orgDashboardMetricVisibility = useMemo(
    () => getOrgDashboardMetricVisibility(orgDashboardVariant),
    [orgDashboardVariant],
  )
  const organizations = useMemo(
    () =>
      access.isReady && access.isFlagEnabled
        ? buildOrgProgramSwitcherTree(rawOrganizations, {
            canSelectOrg: access.canViewOrgDashboard,
            identityScopes: access.identityScopes,
            allowedPartnerIds,
          })
        : [],
    [access, allowedPartnerIds, rawOrganizations],
  )
  const orgsUnavailable =
    access.isReady &&
    access.isFlagEnabled &&
    orgsFetchSettled &&
    (orgsLoadFailed || organizations.length === 0)

  const dashboardRouteState = useMemo(
    () =>
      isDashboardPath && isRouteReady
        ? resolveDashboardRouteState({
            searchParams: new URLSearchParams(currentSearchQuery),
            selectedClientId,
            access,
            organizations,
            programPartnerId: dashboardProgramId,
            programDataUnavailable: partnersLoadFailed,
            orgsFetchSettled,
            orgsLoadFailed,
            orgsLoading,
          })
        : null,
    [
      access,
      currentSearchQuery,
      dashboardProgramId,
      isDashboardPath,
      isRouteReady,
      organizations,
      orgsFetchSettled,
      orgsLoadFailed,
      orgsLoading,
      partnersLoadFailed,
      selectedClientId,
    ],
  )

  const dashboardNavigation = useMemo(
    () =>
      resolveDashboardNavigationState({
        pathname,
        searchParams: new URLSearchParams(currentSearchQuery),
        access,
        routeState: dashboardRouteState,
      }),
    [access, currentSearchQuery, dashboardRouteState, pathname],
  )
  const genericOrgPartnerId = isDashboardRoute
    ? null
    : normalizeNullableClientId(new URLSearchParams(currentSearchQuery).get('org'))
  const isOrgContext = dashboardNavigation.isOrgContext
  const isProgramScopedNavDisabled =
    dashboardNavigation.isProgramScopedNavDisabled || genericOrgPartnerId !== null

  const switcherSearchQuery = useMemo(() => {
    if (dashboardRouteState?.kind !== 'redirect') return currentSearchQuery
    return new URL(dashboardRouteState.target.href, 'https://client.local').searchParams.toString()
  }, [currentSearchQuery, dashboardRouteState])
  const selection = useMemo(() => {
    const params = new URLSearchParams(switcherSearchQuery)
    return resolveSwitcherSelection({
      organizations,
      orgParam: params.get('org'),
      partnersParam: params.get('partners'),
      selectedClientId,
      isOrgContext,
      canSelectOrg: access.canViewOrgDashboard,
    })
  }, [
    access.canViewOrgDashboard,
    isOrgContext,
    organizations,
    selectedClientId,
    switcherSearchQuery,
  ])

  const capabilityPartnerId =
    resolvedPortalContext?.context.kind === 'partner'
      ? resolvedPortalContext.context.partnerId
      : null
  const dashboardHref = useMemo(() => {
    const fromSelection = buildDashboardHrefFromSelection(selection)
    if (fromSelection) return fromSelection
    if (programDashboardState.kind === 'program') return programDashboardState.canonicalHref
    if (capabilityPartnerId) {
      return buildProgramContextDashboardUrl(capabilityPartnerId)
    }
    return dashboardRouteState?.kind === 'redirect'
      ? dashboardRouteState.target.href
      : CLIENT_PAGES.dashboard
  }, [capabilityPartnerId, dashboardRouteState, programDashboardState, selection])

  useLayoutEffect(() => {
    if (!enabled || !isRouteReady) return

    let canonicalHref: string | null = null
    let canonicalProgramId: string | null = null
    let canonicalOrgId: string | null = null
    // A one-program org stays pointed at that program, so program-scoped pages keep working
    // while org context is active. A multi-program org stores the primary partner so Profile
    // and Users keep the same org flags.
    let orgScopedProgramId: string | null = null

    if (genericOrgPartnerId) {
      canonicalHref = buildOrgDashboardUrl(genericOrgPartnerId)
    } else if (dashboardRouteState?.kind === 'redirect') {
      canonicalHref = dashboardRouteState.target.href
      canonicalProgramId = dashboardRouteState.target.programId
    } else if (dashboardRouteState?.kind === 'org') {
      canonicalOrgId = dashboardRouteState.selection.primaryPartnerId
      const { programIds } = dashboardRouteState.selection
      orgScopedProgramId = programIds.length === 1 ? (programIds[0] ?? null) : canonicalOrgId
    } else if (routeIntent.kind === 'program' && programDashboardState.kind === 'program') {
      canonicalHref = programDashboardState.canonicalHref
      canonicalProgramId = programDashboardState.partnerId
    }

    if (canonicalOrgId) {
      if (selectedClientId !== orgScopedProgramId) setSelectedClientId(orgScopedProgramId)
      if (lastSyncedOrgGroupRef.current !== canonicalOrgId) {
        lastSyncedOrgGroupRef.current = canonicalOrgId
        syncPostHogPartnerGroup(canonicalOrgId)
      }
    } else if (canonicalProgramId) {
      lastSyncedOrgGroupRef.current = null
      if (selectedClientId !== canonicalProgramId) setSelectedClientId(canonicalProgramId)
    } else if (routeIntent.kind === 'selected' && routeIntent.partnerId) {
      lastSyncedOrgGroupRef.current = null
    }

    if (!canonicalHref) {
      lastCanonicalCommandRef.current = null
      return
    }

    const currentHref = currentSearchQuery ? `${pathname}?${currentSearchQuery}` : pathname
    if (canonicalHref === currentHref) {
      lastCanonicalCommandRef.current = null
      return
    }
    const commandKey = `${currentHref}->${canonicalHref}`
    if (lastCanonicalCommandRef.current === commandKey) return
    lastCanonicalCommandRef.current = commandKey
    router.replace(canonicalHref, { scroll: false })
  }, [
    currentSearchQuery,
    dashboardRouteState,
    enabled,
    genericOrgPartnerId,
    isRouteReady,
    pathname,
    programDashboardState,
    routeIntent,
    router,
    selection,
    selectedClientId,
    setSelectedClientId,
  ])

  // The session uses the selected partner. While the dashboard route loads, the session
  // does not use the bootstrap partner.
  const selectionPartnerId = resolveSwitcherSelectionPartnerId(selection)
  const orgPartnerPending = isDashboardPath && dashboardRouteState?.kind === 'loading'
  const sessionPartnerId = selectionPartnerId ?? (orgPartnerPending ? null : capabilityPartnerId)

  const featureFlagsState = useMemo<ClientFeatureFlagsState>(() => {
    if (!resolvedPortalContext) return { kind: 'loading' }
    const partner =
      resolvedPortalContext.context.kind === 'partner'
        ? findPartner(partners, resolvedPortalContext.context.partnerId)
        : undefined
    return {
      kind: 'ready',
      context: resolvedPortalContext.context,
      flags: getPartnerFlags(partner),
      error: partnersLoadFailed ? (partnersError ?? 'Failed to load partners') : null,
      refetch: refetchPartners,
    }
  }, [partners, partnersError, partnersLoadFailed, refetchPartners, resolvedPortalContext])

  const value = useMemo<ClientPortalContextValue>(() => {
    const showNestedSwitcher =
      access.isReady &&
      access.isFlagEnabled &&
      !orgsUnavailable &&
      (!orgsFetchSettled || organizations.length > 0)
    const showFlatSwitcher = access.isReady && (!access.isFlagEnabled || orgsUnavailable)
    const switcherLoading =
      !isRouteReady ||
      !access.isReady ||
      partnersLoading ||
      (showNestedSwitcher && organizations.length === 0 && (!orgsFetchSettled || orgsLoading))

    return {
      access,
      dashboardRouteState,
      programDashboardState: routeIntent.kind === 'program' ? programDashboardState : null,
      organizations,
      partners,
      partnersLoading,
      selection,
      capabilityPartnerId,
      dashboardHref,
      isDashboardPath,
      isDashboardRoute,
      isOrgContext,
      isProgramScopedNavDisabled,
      isProgramScopedRouteRedirecting: genericOrgPartnerId !== null,
      showNestedSwitcher,
      showFlatSwitcher,
      switcherLoading,
      orgDashboardVariant,
      orgDashboardMetricVisibility,
    }
  }, [
    access,
    capabilityPartnerId,
    dashboardHref,
    dashboardRouteState,
    isDashboardPath,
    isDashboardRoute,
    isOrgContext,
    isProgramScopedNavDisabled,
    genericOrgPartnerId,
    isRouteReady,
    organizations,
    orgDashboardMetricVisibility,
    orgDashboardVariant,
    orgsFetchSettled,
    orgsLoading,
    orgsUnavailable,
    partners,
    partnersLoading,
    programDashboardState,
    routeIntent.kind,
    selection,
  ])

  return (
    <ClientPortalContext.Provider value={value}>
      <ClientFeatureFlagsProvider enabled={enabled} state={featureFlagsState}>
        {enabled ? (
          <ClientPortalSessionLengthEffect
            partners={partners}
            partnersSettled={partnersFetchSettled}
            partnersLoadFailed={partnersLoadFailed}
            partnerId={sessionPartnerId}
          />
        ) : null}
        <Suspense fallback={null}>
          <SearchParamsSync pathname={pathname} onChange={syncRouteLocation} />
        </Suspense>
        {children}
      </ClientFeatureFlagsProvider>
    </ClientPortalContext.Provider>
  )
}

export function useClientPortalContext(): ClientPortalContextValue {
  const context = useContext(ClientPortalContext)
  if (!context) {
    throw new Error('useClientPortalContext must be used within a ClientPortalContextProvider')
  }
  return context
}
